import { useEffect, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { signOut } from '../lib/auth';
import { callerTeamAccess, DEACTIVATED_TEAM_REASON } from '../lib/teamAccess';
import BasicLoadingScreen from './BasicLoadingScreen';

interface AuthGuardProps {
  children: React.ReactNode;
}

export default function AuthGuard({ children }: AuthGuardProps) {
  const { user, loading } = useAuth();
  const [access, setAccess] = useState<'checking' | 'allowed' | 'deactivated'>('checking');
  const blocked = useRef(false);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      if (!blocked.current) setAccess('checking');
      return;
    }

    let cancelled = false;
    setAccess('checking');
    callerTeamAccess().then(async (status) => {
      if (cancelled) return;
      if (status === 'deactivated') {
        blocked.current = true;
        setAccess('deactivated');
        await signOut();
        return;
      }
      setAccess('allowed');
    });

    return () => {
      cancelled = true;
    };
  }, [user?.id, loading]);

  if (access === 'deactivated' || blocked.current) {
    return (
      <Navigate
        to="/account-disabled"
        replace
        state={{ reason: DEACTIVATED_TEAM_REASON, email: user?.email }}
      />
    );
  }

  if (loading || (user && access === 'checking')) {
    return <BasicLoadingScreen />;
  }

  if (user) {
    return <>{children}</>;
  }

  return <Navigate to="/login" replace />;
}
